import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { firestore } from '../services/firebase';
import { queryClient } from './queryClient';
import { CACHE_TIME } from './cachePolicy';

export const CATEGORIES_QUERY_KEY = ['categories'];
// Lista de todos os vídeos usada pela busca web (ver useWebSearch). Mora aqui
// para `refreshCategories` invalidá-la junto sem import circular.
export const SEARCH_ALL_VIDEOS_QUERY_KEY = ['searchAllVideos'];

/**
 * Categorias com os cards de cada uma (1 leitura da coleção + 1 por categoria).
 * Como é a consulta mais pesada da tela inicial, fica em cache do react-query e
 * é pré-carregada no boot (ver `Navigation`) — antes ela só começava quando a
 * Main montava, e a lista aparecia depois do resto da página.
 */
const fetchCategoriesWithCards = async () => {
  const response = await getDocs(
    query(collection(firestore, 'categories'), orderBy('order', 'asc'))
  );

  return Promise.all(
    response.docs.map(async (catDoc) => {
      const cardsSnap = await getDocs(
        collection(firestore, 'categories', catDoc.id, 'cards')
      );
      const cards = cardsSnap.docs.map((c) => ({ id: c.id, ...c.data() }));

      return {
        id: catDoc.id,
        ...catDoc.data(),
        cards,
      };
    })
  );
};

export const useCategories = () => {
  return useQuery({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: fetchCategoriesWithCards,
    // A consulta mais cara da tela inicial (1 leitura da coleção + 1 por
    // categoria), relida a cada 5 minutos de navegação antes disto. O painel
    // invalida em `refreshCategories` ao publicar conteúdo.
    staleTime: CACHE_TIME.CONTENT,
  });
};

/**
 * Recarrega a lista depois de criar/editar/arquivar conteúdo. Substitui o
 * antigo `findAllCategories()` passado como `onRefreshCategories`; devolve a
 * promise do refetch, então continua podendo ser aguardado.
 *
 * A lista de vídeos da busca só fica marcada como velha (ver
 * `refreshSearchVideos`).
 */
export const refreshCategories = () =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY }),
    refreshSearchVideos(),
  ]);

/**
 * Depois de criar/editar/excluir vídeo. Ninguém observa essa lista, então ela
 * só fica marcada como velha e é relida na próxima busca — não agora.
 */
export const refreshSearchVideos = () =>
  queryClient.invalidateQueries({ queryKey: SEARCH_ALL_VIDEOS_QUERY_KEY });
